import { Lightbulb, Star, Target } from "lucide-react";
import { fmtEUR } from "../lib/calculations";
import { PRELEVEMENTS_SOCIAUX, TMI_OPTIONS, type ComputedResults, type SimulationInput } from "../lib/types";

interface Props {
  results: ComputedResults;
  input: SimulationInput;
}

export function TmiComparator({ results, input }: Props) {
  const baseReel = Math.max(0, results.resultatFiscalReel);
  const baseMicro = results.revenusBrutsAnnuels * 0.5;

  const rows = TMI_OPTIONS.map((tmi) => {
    const taux = tmi + PRELEVEMENTS_SOCIAUX;
    const impotReel = baseReel * taux;
    const impotMicro = baseMicro * taux;
    const meilleur = impotReel <= impotMicro ? "reel" : "micro";
    const ecart = Math.abs(impotReel - impotMicro);
    return { tmi, taux, impotReel, impotMicro, meilleur, ecart };
  });

  // TMI où l'écart entre régimes est le plus important = recommandation la plus impactante
  const maxEcart = Math.max(...rows.map((r) => r.ecart));
  const recommandee = rows.find((r) => r.ecart === maxEcart && maxEcart > 0)?.tmi;

  return (
    <section className="mx-auto max-w-6xl px-6 py-8 lg:px-12">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h3 className="flex items-center gap-2 text-lg font-bold text-foreground">
          <Target className="h-5 w-5 text-primary" aria-hidden />
          Impôt LMNP selon votre tranche (TMI)
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Estimation pour chaque tranche d'imposition + prélèvements sociaux 17,2%. Votre TMI
          actuelle :{" "}
          <strong className="text-foreground">{Math.round(input.tmi * 100)}%</strong>.
        </p>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 font-medium">TMI</th>
                <th className="py-2 font-medium">Taux global</th>
                <th className="py-2 font-medium">Régime réel</th>
                <th className="py-2 font-medium">Micro-BIC</th>
                <th className="py-2 font-medium">Plus avantageux</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => {
                const isCurrent = Math.abs(r.tmi - input.tmi) < 0.001;
                const isRecommandee = recommandee !== undefined && Math.abs(r.tmi - recommandee) < 0.001;
                return (
                  <tr
                    key={r.tmi}
                    className={isCurrent ? "bg-primary/5" : isRecommandee ? "bg-success/5" : ""}
                  >
                    <td className="py-2.5 font-semibold text-foreground tabular">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span>{Math.round(r.tmi * 100)}%</span>
                        {isCurrent && (
                          <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground">
                            Vous
                          </span>
                        )}
                        {isRecommandee && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-success px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success-foreground">
                            <Star className="h-2.5 w-2.5" aria-hidden /> Écart max
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 tabular text-muted-foreground">
                      {(r.taux * 100).toFixed(1)}%
                    </td>
                    <td className="py-2.5 tabular text-foreground">{fmtEUR(r.impotReel)}</td>
                    <td className="py-2.5 tabular text-foreground">{fmtEUR(r.impotMicro)}</td>
                    <td className="py-2.5">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          r.meilleur === "reel"
                            ? "bg-success/15 text-success"
                            : "bg-primary/15 text-primary"
                        }`}
                      >
                        {r.meilleur === "reel" ? "Régime réel" : "Micro-BIC"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mini bar chart : impôt réel vs micro-BIC par TMI */}
        <div className="mt-6 rounded-xl border border-border bg-secondary/30 p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Visualisation des écarts
            </p>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-success" />
                Régime réel
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-primary" />
                Micro-BIC
              </span>
            </div>
          </div>
          <div className="space-y-3">
            {rows.map((r) => {
              const max = Math.max(r.impotReel, r.impotMicro, 1);
              const wReel = (r.impotReel / max) * 100;
              const wMicro = (r.impotMicro / max) * 100;
              const isCurrent = Math.abs(r.tmi - input.tmi) < 0.001;
              const isReco = recommandee !== undefined && Math.abs(r.tmi - recommandee) < 0.001;
              return (
                <div key={r.tmi} className="grid grid-cols-[40px_1fr] items-center gap-3">
                  <div className={`text-xs font-semibold tabular ${isCurrent ? "text-primary" : isReco ? "text-success" : "text-muted-foreground"}`}>
                    {Math.round(r.tmi * 100)}%
                  </div>
                  <div className="space-y-1">
                    <div className="relative h-3.5 rounded bg-background">
                      <div
                        className="h-full rounded bg-success transition-all"
                        style={{ width: `${wReel}%` }}
                      />
                      <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] font-medium tabular text-foreground/80">
                        {fmtEUR(r.impotReel)}
                      </span>
                    </div>
                    <div className="relative h-3.5 rounded bg-background">
                      <div
                        className="h-full rounded bg-primary transition-all"
                        style={{ width: `${wMicro}%` }}
                      />
                      <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] font-medium tabular text-foreground/80">
                        {fmtEUR(r.impotMicro)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="mt-4 flex items-start gap-1.5 text-xs text-muted-foreground">
          <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
          <span>Ajustez votre TMI dans le mode avancé (étape 4) pour recalculer toutes les métriques.</span>
        </p>
      </div>
    </section>
  );
}
